import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, 
  TablePagination, Chip, Button, Grid, TextField, MenuItem, Autocomplete, CircularProgress
} from '@mui/material';
import { Download, Search } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import moment from 'moment';
import api from '../../api';

const MeetingAttendeesReport = () => {
  const [data, setData] = useState([]);
  const [masters, setMasters] = useState({ purposes: [], depts: [], employees: [] });
  const [loading, setLoading] = useState(false);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    start_date: moment().startOf('month').format('YYYY-MM-DD'),
    end_date: moment().endOf('month').format('YYYY-MM-DD'),
    status: '', department_id: '', purpose_id: '', employee_ids: []
  });

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [purpRes, deptRes, empRes] = await Promise.all([
          api.get('/meeting-views/purposes'),
          api.get('/meeting-views/departments'),
          api.get('/employees')
        ]);
        
        const rawEmployees = empRes.data.data || empRes.data || [];
        // Filter strictly for ACTIVE and NOTICE employees
        const eligibleEmployees = rawEmployees.filter(e => {
            const status = String(e.status || e.emp_status || e.employee_status || '').toUpperCase();
            return status === 'ACTIVE' || status === 'NOTICE';
        });

        setMasters({ 
          purposes: purpRes.data, 
          depts: deptRes.data, 
          employees: eligibleEmployees 
        });
      } catch (err) {
        console.error("Failed to fetch masters", err);
      }
    };
    fetchMasters();
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    setPage(0);
    try {
      const payload = {
        ...filters,
        employee_ids: filters.employee_ids.map(e => e.emp_id),
        department_id: filters.department_id || null,
        purpose_id: filters.purpose_id || null
      };
      const res = await api.post('/meeting-views/attendees-report', payload);
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch report", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!data.length) return;
    const exportData = data.map(row => ({
      "Meeting ID": row.meeting_id,
      "Organized By": `${row.organizer_name} (${row.organizer_id})`,
      "Employee Name": row.employee_name,
      "Employee ID": row.employee_id,
      "Date": row.meeting_date,
      "Time": `${moment(row.start_time, 'HH:mm:ss').format('hh:mm A')} - ${moment(row.end_time, 'HH:mm:ss').format('hh:mm A')}`,
      "Total Minutes": row.duration_minutes,
      "Purpose": row.purpose,
      "Status": row.status
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendees_Report");
    XLSX.writeFile(wb, `Attendees_Report_${moment().format('YYYYMMDD')}.xlsx`);
  };

  const getStatusColor = (status) => {
    return status === 'SCHEDULED' ? 'primary' : status === 'COMPLETED' ? 'success' : 'error';
  };

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="From Date" value={filters.start_date} onChange={(e) => setFilters({...filters, start_date: e.target.value})} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="To Date" value={filters.end_date} onChange={(e) => setFilters({...filters, end_date: e.target.value})} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField fullWidth size="small" select label="Status" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth size="small" select label="Department" value={filters.department_id} onChange={(e) => setFilters({...filters, department_id: e.target.value})}>
              <MenuItem value=""><em>All</em></MenuItem>
              {masters.depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField fullWidth size="small" select label="Purpose" value={filters.purpose_id} onChange={(e) => setFilters({...filters, purpose_id: e.target.value})}>
              <MenuItem value=""><em>All</em></MenuItem>
              {masters.purposes.map(p => <MenuItem key={p.id} value={p.id}>{p.purpose_name}</MenuItem>)}
            </TextField>
          </Grid>
          
          <Grid item xs={12} md={9}>
            <Autocomplete
              multiple size="small"
              options={masters.employees}
              getOptionLabel={(o) => `${o.emp_id} - ${o.first_name} ${o.last_name || ''}`.trim()}
              isOptionEqualToValue={(o, v) => o.emp_id === v.emp_id}
              value={filters.employee_ids}
              onChange={(e, val) => setFilters({...filters, employee_ids: val})}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip variant="outlined" size="small" label={option.first_name} {...getTagProps({ index })} key={option.emp_id} />
              ))}
              renderInput={(params) => <TextField {...params} label="Search & Select Employees (Active/Notice Only)" />}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
            <Button variant="contained" onClick={handleSearch} startIcon={<Search />} fullWidth disabled={loading}>
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" color="primary">Attendees Report</Typography>
          <Button variant="outlined" color="success" startIcon={<Download />} onClick={handleExport} disabled={data.length === 0}>
            Export Excel
          </Button>
        </Box>

        {loading ? <CircularProgress sx={{ display: 'block', m: 'auto', my: 5 }} /> : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Meeting ID</TableCell>
                  <TableCell>Organized By</TableCell>
                  <TableCell>Employee Name & ID</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell align="center">Total Minutes</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>{row.meeting_id}</TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">{row.organizer_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{row.organizer_id}</Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">{row.employee_name}</Typography>
                      <Typography variant="caption" color="textSecondary">{row.employee_id}</Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">{moment(row.meeting_date).format('DD MMM YYYY')}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {moment(row.start_time, 'HH:mm:ss').format('hh:mm A')} - {moment(row.end_time, 'HH:mm:ss').format('hh:mm A')}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip label={`${row.duration_minutes} Mins`} size="small" variant="outlined" />
                    </TableCell>
                    
                    <TableCell>{row.purpose}</TableCell>
                    
                    <TableCell>
                      <Chip size="small" label={row.status} color={getStatusColor(row.status)} sx={{ fontSize: '0.7rem' }} />
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No attendees found for the selected filters. Click "Generate Report".</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination component="div" count={data.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </Paper>
    </Box>
  );
};

export default MeetingAttendeesReport;